import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Accordion, AccordionDetails, AccordionSummary, Box, Slide, Typography } from "@mui/material"
import React, { useCallback, useContext, useEffect, useMemo } from "react"
import { customPalette } from "../../../../../../../../theme"
import { SquareBadge } from "../../../../../../../components/badges/SquareBadge"
import { BSCalculStatusChip } from "../../../../../../../components/card/beem-shot/BSCalculStatusChip"
import { IconBox } from "../../../../../../../components/custom-icons/icon-box"
import { IconCardInfo } from "../../../../../../../components/custom-icons/icon-card-info"
import { IconCubeOff } from "../../../../../../../components/custom-icons/icon-cube-off"
import { IconFondations } from "../../../../../../../components/custom-icons/icon-fondations"
import { IconFrame } from "../../../../../../../components/custom-icons/icon-frame"
import { IconLevelAndZone } from "../../../../../../../components/custom-icons/icon-level-zone"
import { IconParking } from "../../../../../../../components/custom-icons/icon-parking"
import { theme as bsTheme } from "../../../../../../../components/layout/BSLayout"
import { TabEnum } from "../../../../../../../components/tabs/tabs"
import { BSBuildingResultContext } from "../../../../../../../core/context/beem-shot/BSBuildingResult/BSBuildingResultContext"
import { BSBuilding } from "../../../../../../../core/dto/beem-shot/BSBuilding/BSBuilding"
import { CalculStatusEnum } from "../../../../../../../core/enum/calculStatusEnum"
import { greenSelectableStyle, truncateTextCss } from "../../../../../../../core/services/helper-service"
import { BSBuildingMorphologyContext } from "../../tabs/BSBuildingTabs/BSBuildingMorphologyTab/context/BSBuildingMorphologyContext"
import { useAccordion } from "../BSCalculationPage/BSCalculationForm/components/AdvancedQuestionnaire/useAccordion"
import { BSLevelContext } from "../BSCreateBuildingPages/BSZoneAndLevelPage/context/BSLevelContext"
import { BSLabelWithIcon } from "./BSLabelWithIcon"
import { BSUsageList } from "./BSUsageList"
import { BuildingTab } from "./BuildingTab"
import { BSBuildingContext } from "./context/BSBuildingContext"

interface IProps {
  bsBuilding: BSBuilding
  selectedTab: number
  handleSelectTab(index: TabEnum, buildingId?: string): void
}

export function BSBuildingAccordion({ bsBuilding, selectedTab, handleSelectTab }: Readonly<IProps>): React.JSX.Element {
  const { expanded: expandedBSBuilding, handleExpanded: handleBSBuildingExpanded, expand } = useAccordion()
  const { selectBSBuilding, selectedBSBuilding } = useContext(BSBuildingContext)
  const { startEstimatingMorphology, bsBuildingMorphology, isMorphologyLoaded } = useContext(BSBuildingMorphologyContext)
  const { bsBuildingResult } = useContext(BSBuildingResultContext)
  const { groupLevelSuperstructureList } = useContext(BSLevelContext)

  const rowSelectableStyle = useMemo(
    () => greenSelectableStyle(expandedBSBuilding === bsBuilding.id),
    [bsBuilding.id, expandedBSBuilding]
  )

  useEffect(() => {
    if (
      !selectedBSBuilding &&
      (selectedTab === TabEnum.BS_VARIANT_PARCELLE ||
        selectedTab === TabEnum.BS_VARIANT_INFO ||
        selectedTab === TabEnum.BS_VARIANT_DASHBOARD)
    ) {
      expand("")
      selectBSBuilding("")
    }
  }, [expand, selectedBSBuilding, selectBSBuilding, selectedTab])

  useEffect(() => {
    if (selectedBSBuilding) {
      expand(selectedBSBuilding?.id)
    } else {
      expand("")
      selectBSBuilding("")
    }
  }, [expand, selectedBSBuilding, selectBSBuilding])

  const isMorphologyDefined: boolean = useMemo(
    () =>
      (!bsBuilding?.bim && isMorphologyLoaded && !!bsBuildingMorphology) || (bsBuilding?.bim && !!bsBuilding.bsBimModelId),
    [bsBuilding?.bim, bsBuilding.bsBimModelId, bsBuildingMorphology, isMorphologyLoaded]
  )

  const isLevelAndZoneDefined: boolean = useMemo(
    () => (!bsBuilding?.bim && groupLevelSuperstructureList.length !== 0) || bsBuilding?.bim,
    [bsBuilding?.bim, groupLevelSuperstructureList.length]
  )

  const isQuestionnaireFilled: boolean = useMemo(() => bsBuildingResult !== undefined, [bsBuildingResult])

  const tabsPanel: BuildingTab[] = useMemo(
    () => [
      {
        title: "Tableau de bord",
        icon: DashboardOutlinedIcon,
        tabKey: TabEnum.BUILDING_DASHBOARD,
        disabled: !isMorphologyDefined || !isLevelAndZoneDefined || !isQuestionnaireFilled,
        disabledText: "Veuillez renseigner la morphologie, les niveaux et la configuration ACV",
      },
      {
        title: "Informations",
        icon: IconCardInfo,
        tabKey: TabEnum.BUILDING_INFO,
      },
      {
        title: bsBuilding?.bim ? "Maquette numérique" : "Morphologie",
        icon: bsBuilding?.bim ? IconBox : IconCubeOff,
        tabKey: TabEnum.BUILDING_MORPHOLOGY_OR_BIM,
        showWarning: !isMorphologyDefined,
      },
      {
        title: "Parking",
        icon: IconParking,
        tabKey: TabEnum.BUILDING_PARKING,
        disabled: !isMorphologyDefined,
        disabledText: "Veuillez renseigner la morphologie",
      },
      {
        title: "Niveaux et zones",
        icon: IconLevelAndZone,
        tabKey: TabEnum.BUILDING_LEVEL,
        showWarning: !isLevelAndZoneDefined,
        disabled: !isMorphologyDefined,
        disabledText: "Veuillez renseigner la morphologie",
      },
      // Si l’utilisateur n’a pas de BIM, on insère « Patios »
      ...(!bsBuilding?.bim
        ? [
            {
              title: "Patios",
              icon: IconFrame,
              tabKey: TabEnum.BUILDING_PATIO,
              disabled: !isMorphologyDefined || !isLevelAndZoneDefined,
              disabledText: "Veuillez renseigner la morphologie et les niveaux",
            },
          ]
        : []),
      {
        title: "Fondations",
        icon: IconFondations,
        tabKey: TabEnum.BUILDING_FONDATIONS,
        disabled: !isMorphologyDefined || !isLevelAndZoneDefined,
        disabledText: "Veuillez renseigner la morphologie et les niveaux",
      },
    ],
    [bsBuilding?.bim, isLevelAndZoneDefined, isMorphologyDefined, isQuestionnaireFilled]
  )

  const navigateToDashboard: () => void = useCallback(() => {
    if (bsBuilding.calculStatus !== CalculStatusEnum.READY) {
      startEstimatingMorphology(bsBuilding.id)
      handleSelectTab(TabEnum.BUILDING_DASHBOARD)
    } else {
      handleSelectTab(TabEnum.BUILDING_DASHBOARD)
    }
  }, [bsBuilding.calculStatus, bsBuilding.id, handleSelectTab, startEstimatingMorphology])

  return (
    <Accordion
      disableGutters
      square
      elevation={0}
      expanded={expandedBSBuilding === bsBuilding.id}
      onChange={(e, isExpanded) => {
        if (isExpanded) {
          handleBSBuildingExpanded(bsBuilding.id)
          selectBSBuilding(bsBuilding.id)
          handleSelectTab(TabEnum.BUILDING_INFO, bsBuilding.id)
        } else {
          handleBSBuildingExpanded("")
          selectBSBuilding("")
          handleSelectTab(TabEnum.BS_VARIANT_INFO)
        }
      }}
      sx={{
        ...rowSelectableStyle,
        width: "100%",
        borderRadius: "14px",
        border: `solid 3px ${customPalette.lightGray7}`,
        backgroundColor: customPalette.lightGray6,
        "&:hover": {
          cursor: "pointer",
        },
      }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ ...rowSelectableStyle, backgroundColor: undefined }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Box>
              <BSCalculStatusChip
                diffIc2022={bsBuilding.diffIc2022}
                diffIc2025={bsBuilding.diffIc2025}
                diffIc2028={bsBuilding.diffIc2028}
                diffIc2031={bsBuilding.diffIc2031}
                higherSeuilValide={bsBuilding.higherSeuilValide}
                calculStatus={bsBuilding.calculStatus}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                color: customPalette.darkBlue3,
              }}>
              {bsBuilding.bim ? (
                <SquareBadge icon={<IconBox />} backgroundColor={bsTheme.palette.primary.main} />
              ) : (
                <SquareBadge icon={<IconCubeOff />} backgroundColor={customPalette.lightGray6} />
              )}

              <Typography variant="subtitle2" fontSize={14} component="span" sx={{ truncateTextCss }}>
                {bsBuilding.name}
              </Typography>
            </Box>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ borderTop: `3px solid ${customPalette.lightWhite}`, p: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {tabsPanel.map((tab, index) => (
            <Slide
              direction="right"
              key={tab.tabKey}
              in={expandedBSBuilding === bsBuilding.id}
              style={{ transitionDelay: `${index * 80}ms` }}>
              <Box>
                <BSLabelWithIcon
                  title={tab.title}
                  icon={tab.icon}
                  isSelectedTab={selectedTab === tab.tabKey}
                  onClick={() => {
                    if (tab.tabKey === TabEnum.BUILDING_DASHBOARD) {
                      navigateToDashboard()
                    } else {
                      handleSelectTab(tab.tabKey)
                    }
                  }}
                  disabled={tab.disabled}
                  disabledText={tab.disabledText}
                  showWarning={tab.showWarning}
                />
              </Box>
            </Slide>
          ))}
        </Box>

        <BSUsageList
          isDisabled={!isLevelAndZoneDefined}
          showWarning={isMorphologyDefined && isLevelAndZoneDefined && !isQuestionnaireFilled}
        />
      </AccordionDetails>
    </Accordion>
  )
}
